'use client'

import { useToast, pauseToastOnHover, resumeToastOnLeave } from '@/hooks/use-toast'
import {
  Toast,
  ToastClose,
  ToastCopy,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/notifications/toast'
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

function ToastStatusIcon({ variant }: { variant?: 'default' | 'success' | 'destructive' | 'warning' | 'loading' }) {
  const iconClass = 'h-5 w-5 shrink-0'
  switch (variant) {
    case 'success':
      return <CheckCircle2 className={cn(iconClass, 'text-[hsl(var(--intent-success))]')} aria-hidden />
    case 'destructive':
      return <XCircle className={cn(iconClass, 'text-destructive')} aria-hidden />
    case 'warning':
      return <AlertTriangle className={cn(iconClass, 'text-[hsl(var(--intent-warning))]')} aria-hidden />
    case 'loading':
      return <Loader2 className={cn(iconClass, 'animate-spin text-muted-foreground')} aria-hidden />
    case 'default':
    default:
      return <Info className={cn(iconClass, 'text-muted-foreground')} aria-hidden />
  }
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        const hasContent = title || description

        return (
          <Toast
            key={id}
            variant={variant}
            {...props}
            onMouseEnter={() => pauseToastOnHover(id)}
            onMouseLeave={() => resumeToastOnLeave(id)}
          >
            <div className="flex-1 grid gap-1.5 min-w-0">
              {title && (
                <div className="flex items-center gap-2">
                  <ToastStatusIcon variant={variant ?? undefined} />
                  <ToastTitle className="flex-1 min-w-0">{title}</ToastTitle>
                </div>
              )}
              {description && !title && (
                <div className="flex items-start gap-2">
                  <ToastStatusIcon variant={variant ?? undefined} />
                  <ToastDescription className="flex-1 min-w-0">{description}</ToastDescription>
                </div>
              )}
              {description && title && (
                <ToastDescription className="pl-7">{description}</ToastDescription>
              )}
            </div>
            {action && <div className="flex-shrink-0 self-center">{action}</div>}
            {hasContent && (
              <ToastCopy
                title={typeof title === 'string' ? title : undefined}
                description={typeof description === 'string' ? description : undefined}
              />
            )}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}

